/**
 * `CancelBookingModal` (C3, 2026-07-29) — the ONE cancel-a-booking affordance.
 *
 * Until now `cancel_booking` was dispatched from exactly one place in the SPA:
 * an inline control cluster buried inside the calendar's `SlotModal`. Mario
 * wanted the same cancel on the member profile's Upcoming Sessions card, and
 * the honest way to get it is to lift the cluster out rather than write a
 * second dispatch — the wire has two subtleties a copy would drift on:
 *
 *   - `credit_handling` is the staff OVERRIDE of the studio's late-cancel
 *     policy: `"return"` gives the credit back even inside the 24h window,
 *     `"consume"` keeps it spent even outside it. The chokepoint honours it
 *     only for staff/CLI callers.
 *   - `notify_member` is threaded ONLY when suppressing. The action's gate
 *     reads an explicit `false`; absence means "notify", so sending
 *     `notify_member: true` and omitting it are the same thing — but sending
 *     `false` by accident silently loses the member's message.
 *
 * The dispatch answers 202 + a `run_id`, so the component owns a `useSagaRun`
 * poll and only calls `onDone` once the saga is really terminal — a caller that
 * refreshed on the 202 would re-read the booking before the cancel applied and
 * show it as still active.
 *
 * Rendering is one flat cluster in both homes (calendar list row / member-page
 * modal); only the wrapper class differs. Every child is phrasing content
 * inside a `<span>` because the calendar site nests it in a `<li> > <span>`,
 * where a `<div>`/`<p>` would be invalid HTML.
 */

import { useCallback, useEffect, useState } from "react";
import { dispatchAction } from "../lib/dispatch";
import { isWithinCancellationWindow } from "../lib/studio-policy";
import { useSagaRun } from "../lib/use-saga-run";

export interface CancelBookingModalProps {
  readonly bookingId: string;
  /**
   * The session's start (ISO). Present ⇒ the cluster warns when the cancel
   * falls inside the studio's cancellation window, so "Return credit" reads as
   * the deliberate override it is rather than the obvious default.
   */
  readonly startsAt?: string;
  /** Optional prose above the controls — the modal host wants it, the inline calendar row does not. */
  readonly hint?: string;
  /** Wrapper class; defaults to the calendar's inline cluster. */
  readonly className?: string;
  /** Disable the controls while an unrelated saga on the same slot is running. */
  readonly disabled?: boolean;
  /** Mirrors in-flight state upward (the calendar gates its "Book in" button on it). */
  readonly onBusyChange?: (busy: boolean) => void;
  /** The saga reached `done` — refresh whatever list this sits in. */
  readonly onDone: () => void;
  /** "Keep" — dismiss without cancelling. */
  readonly onClose: () => void;
}

export function CancelBookingModal({
  bookingId,
  startsAt,
  hint,
  className = "cal-cancel-choice",
  disabled = false,
  onBusyChange,
  onDone,
  onClose,
}: CancelBookingModalProps): JSX.Element {
  // Defaults ON — the member SHOULD hear their session was cancelled.
  const [notify, setNotify] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useSagaRun(runId);

  const busy = dispatching || runId !== null;

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  // React once the polled saga is terminal. Setting `runId` back to null resets
  // `useSagaRun` to its initial `in_progress`, so the guard below stops this
  // from re-firing on the next render.
  useEffect(() => {
    if (runId === null || run.state === "in_progress") return;
    setRunId(null);
    if (run.state === "done") {
      onDone();
      return;
    }
    if (run.state === "failed") {
      setError(`Cancel failed: ${run.last_error ?? "unknown error"}`);
      return;
    }
    if (run.state === "forbidden") {
      // The cancel was dispatched and runs regardless — only the status
      // READ was refused. Do not call it a failure or a timeout.
      setError(
        `The cancel was dispatched, but this account was refused permission to check its progress (${run.last_error ?? "forbidden"}). Refresh to see whether it applied.`,
      );
      return;
    }
    setError(
      "Timed out waiting for the cancel — refresh to check whether it applied.",
    );
  }, [run.state, run.last_error, runId, onDone]);

  const submit = useCallback(
    async (creditHandling: "return" | "consume"): Promise<void> => {
      setDispatching(true);
      setError(null);
      try {
        const res = await dispatchAction<
          {
            booking_id: string;
            credit_handling: "return" | "consume";
            notify_member?: boolean;
          },
          { run_id?: string }
        >("cancel_booking", {
          booking_id: bookingId,
          credit_handling: creditHandling,
          // Only thread the flag when SUPPRESSING — see the header note.
          ...(notify ? {} : { notify_member: false }),
        });
        setDispatching(false);
        if (!res.ok) {
          setError(`Cancel failed: ${res.message || res.code}`);
          return;
        }
        const rid = res.response?.run_id;
        if (typeof rid === "string") {
          setRunId(rid);
          return;
        }
        // No run id — a synchronous outcome; nothing to poll.
        onDone();
      } catch (e) {
        setDispatching(false);
        setError((e as Error).message);
      }
    },
    [bookingId, notify, onDone],
  );

  const locked = disabled || busy;
  const late = startsAt !== undefined && isWithinCancellationWindow(startsAt);

  return (
    <span className={className}>
      {hint !== undefined ? (
        <span className="cancel-booking-line">{hint}</span>
      ) : null}
      {late ? (
        <span className="cancel-booking-late">
          This session is inside the studio&apos;s cancellation window — the
          credit is normally forfeited. &ldquo;Return credit&rdquo; overrules
          that.
        </span>
      ) : null}
      <label
        className="cal-cancel-notify"
        title="Message the member that their session was cancelled"
      >
        <input
          type="checkbox"
          checked={notify}
          disabled={locked}
          onChange={(e) => setNotify(e.target.checked)}
        />{" "}
        Notify member
      </label>
      <button
        type="button"
        className="cal-cancel-return"
        disabled={locked}
        title="Cancel and give the credit back (overrules the late-cancel policy)"
        onClick={() => void submit("return")}
      >
        Return credit
      </button>
      <button
        type="button"
        className="cal-cancel-consume"
        disabled={locked}
        title="Cancel and keep the credit spent (overrules the late-cancel policy)"
        onClick={() => void submit("consume")}
      >
        Use credit
      </button>
      <button
        type="button"
        className="cal-cancel-keep"
        disabled={busy}
        onClick={onClose}
      >
        Keep
      </button>
      {busy ? (
        <span className="cancel-booking-busy">Cancelling…</span>
      ) : null}
      {error !== null ? (
        <span className="cancel-booking-error">{error}</span>
      ) : null}
    </span>
  );
}
